import useCrud, { CrudDataModel, CrudModel } from "./useCrud";
import { RefObject, useEffect, useRef, useState } from "react";
import Select from "react-select/dist/declarations/src/Select";
import { GraphqlFilterRoot } from "../root/general/crudGraphql/domain/models/graphqlModel";

interface EntitySelectModel extends CrudModel {
  labelPrefix: string;
  value?: string | number | undefined | boolean | string[] | number[] | null;
  optionsMap?: (option: CrudDataModel) => CrudDataModel;
  filterReloadOnSearch?: boolean;
  isDisabled?: boolean;
}

interface EntitySelectDataModel {
  ref: RefObject<Select>;
  options: CrudDataModel[];
  loading?: boolean;
  setLoading: (loading: boolean) => void;
  loadOptions: (
    inputValue: string,
    callback: (options: CrudDataModel[]) => void,
  ) => void;
  addOption: (id: string, name: string) => void;
  reload: (
    overrideFilers?: GraphqlFilterRoot | undefined,
  ) => Promise<CrudDataModel[]>;
  findByIdAndAdd: (id: string) => void;
  setDefaultOption: (id: string, label: string) => void;
}

export default function useEntitySelect({
  entity,
  prefix,
  schema,
  page,
  limit,
  filters,
  value,
  labelPrefix,
  optionsMap,
  filterReloadOnSearch = false,
  isDisabled = false,
}: EntitySelectModel): EntitySelectDataModel {
  const ref = useRef<Select>(null);

  const [options, setOptions] = useState<CrudDataModel[]>([]);

  const crud = useCrud({
    entity,
    prefix,
    schema,
    page,
    limit,
    filters,
  });

  const initData = (
    overrideFilters?: GraphqlFilterRoot,
  ): Promise<CrudDataModel[]> => {
    return new Promise((resolve) => {
      void crud.getData(overrideFilters).then((data) => {
        const options = data.map((item) => ({
          value: item.id,
          label: item[labelPrefix],
        }));

        resolve(optionsMap ? options.map(optionsMap) : options);
      });
    });
  };

  useEffect(() => {
    if (filterReloadOnSearch || isDisabled) return;
    void initData().then((options: CrudDataModel[]) => {
      setOptions(options);

      if (options.find((item) => item.value === value)) {
        ref.current?.setValue(
          options.find((item) => item.value === value),
          "select-option",
        );
      }
    });
  }, [filters, isDisabled]);

  useEffect(() => {
    if (value === undefined) {
      ref.current?.setValue(null, "select-option");
    }
  }, [value]);

  const loadOptions = async (
    inputValue: string,
    callback: (options: CrudDataModel[]) => void,
  ) => {
    return crud
      .getData({
        ...filters,
        searchBy: "SearchByRoot" + entity,
        fields: {
          ...(filters?.fields ?? {}),
          [labelPrefix]: {
            contains: inputValue,
          },
        },
      } as GraphqlFilterRoot)
      .then((data) => {
        const options = data.map((item) => ({
          value: item.id,
          label: item[labelPrefix],
        }));

        callback(optionsMap ? options.map(optionsMap) : options);
      });
  };

  const addOption = (id: string, name: string) => {
    setOptions([
      ...options,
      {
        value: id,
        label: name,
      },
    ]);
  };

  const findByIdAndAdd = (id: string) => {
    void crud.getOneData(id, filters).then((data) => {
      if (data == null) return;
      addOption(id, data[labelPrefix].toString());
      setTimeout(() => {
        ref.current?.setValue(
          {
            value: id,
            label: data[labelPrefix],
          },
          "select-option",
        );
      }, 100);
    });
  };

  const setDefaultOption = (id: string, label: string) => {
    setOptions([
      ...options,
      {
        value: id,
        label: label,
      },
    ]);
    setTimeout(() => {
      ref.current?.setValue(
        {
          value: id,
          label: label,
        },
        "select-option",
      );
    }, 100);
  };

  return {
    ref,
    options,
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    loadOptions,
    reload: initData,
    loading: crud.loading,
    setLoading: crud.setLoading,
    addOption: addOption,
    findByIdAndAdd: findByIdAndAdd,
    setDefaultOption: setDefaultOption,
  };
}
